import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  documentTypeOptions,
  ecorpResources,
  extractionStatusOptions,
  outcomeDeterminationOptions,
  processingStatusOptions,
  reportCategoryOptions,
  reviewStatusOptions,
  type EcorpResourceKey,
} from '@command-center/admin-ecorp';
import { Badge, Button, Card, CardContent, Input, usePageHeader } from '@command-center/ui-system';
import { Grid2X2, List, RefreshCw, Search, Upload, X } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { listRecords, type PortalRecord } from '../api';
import {
  ConfidencePill,
  display,
  EmptyState,
  ErrorNotice,
  formatDate,
  Skeletons,
  StatusPill,
} from '../lib';
import { DocumentDetail } from '../components/DocumentDetail';
import { UploadDialog } from '../components/UploadDialog';

type ViewMode = 'cards' | 'list';
type LinkedFilter = { id: string; label: string };

interface DocumentFilters {
  search: string;
  project?: LinkedFilter;
  client?: LinkedFilter;
  documentType: string;
  reportCategory: string;
  outcomeDetermination: string;
  hasIssuesExceedances: string;
  hasMitigationConditions: string;
  processingStatus: string;
  extractionStatus: string;
  reviewStatus: string;
  currentVersion: string;
  documentDateFrom: string;
  documentDateTo: string;
}

const emptyFilters: DocumentFilters = {
  search: '',
  documentType: '',
  reportCategory: '',
  outcomeDetermination: '',
  hasIssuesExceedances: '',
  hasMitigationConditions: '',
  processingStatus: '',
  extractionStatus: '',
  reviewStatus: '',
  currentVersion: '',
  documentDateFrom: '',
  documentDateTo: '',
};

function LinkedRecordFilter({
  resource,
  label,
  primaryField,
  value,
  onChange,
}: {
  resource: Extract<EcorpResourceKey, 'projects' | 'clients'>;
  label: string;
  primaryField: string;
  value?: LinkedFilter;
  onChange(value?: LinkedFilter): void;
}) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PortalRecord[]>([]);
  useEffect(() => {
    const search = query.trim();
    if (!search || value) {
      setOptions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void listRecords(resource, new URLSearchParams({ pageSize: '6', search }))
        .then((page) => setOptions(page.items))
        .catch(() => setOptions([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, resource, value]);
  return (
    <label className="field-label relative">
      {label}
      {value ? (
        <span className="field-control flex items-center justify-between gap-2 text-sm">
          <span className="truncate">{value.label}</span>
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => {
              onChange(undefined);
              setQuery('');
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ) : (
        <>
          <input
            className="field-control"
            value={query}
            placeholder={`Find ${label.toLowerCase()}…`}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() && (
            <span className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border bg-popover p-1 shadow-lg">
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    onChange({
                      id: option.id,
                      label: display(option.fields[primaryField]) || option.id,
                    });
                    setQuery('');
                  }}
                >
                  {display(option.fields[primaryField]) || option.id}
                </button>
              ))}
              {!options.length && (
                <span className="block px-2 py-1.5 text-sm text-muted-foreground">No matches</span>
              )}
            </span>
          )}
        </>
      )}
    </label>
  );
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange(value: string): void;
}) {
  return (
    <label className="field-label">
      {label}
      <select
        className="field-control"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option === 'true' ? 'Yes' : option === 'false' ? 'No' : option}
          </option>
        ))}
      </select>
    </label>
  );
}

function outcomeClass(value: unknown): string {
  const outcome = display(value);
  if (
    outcome === 'Compliant / No Significant Impact' ||
    outcome.startsWith('Less Than Significant') ||
    outcome === 'Approval / Permit Granted'
  )
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
  if (outcome === 'Approval with Conditions')
    return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400';
  if (
    outcome === 'Potentially Significant' ||
    outcome === 'Significant / Noncompliant' ||
    outcome === 'Denied / Not Approved' ||
    outcome === 'Needs Review'
  )
    return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400';
  return 'border-border bg-muted text-muted-foreground';
}

function OutcomeBadge({ value }: { value: unknown }) {
  const text = display(value);
  return text ? (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${outcomeClass(value)}`}
    >
      {text}
    </span>
  ) : null;
}

function IssueBadge({ value }: { value: unknown }) {
  return value === true ? (
    <span className="inline-flex rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
      Issues / exceedances
    </span>
  ) : null;
}

function MitigationBadge({ value }: { value: unknown }) {
  return value === true ? (
    <span className="inline-flex rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
      Mitigation / conditions
    </span>
  ) : null;
}

function DocumentCard({ item, onOpen }: { item: PortalRecord; onOpen(): void }) {
  const fields = item.fields;
  const name = display(fields['Document Name']) || 'Untitled document';
  const reportName = display(fields['Technical Report Name']);
  const preview = display(fields['Findings / Conclusions']) || display(fields['Document Summary']);
  const needsReview =
    display(fields['Processing Status']) === 'Needs Review' ||
    display(fields['Extraction Status']) === 'Needs Review' ||
    ['Not Reviewed', 'In Review', 'Changes Requested'].includes(display(fields['Review Status']));
  return (
    <Card
      className="cursor-pointer transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
      onClick={onOpen}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold leading-snug">{name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {display(fields.Project) || 'No linked project'}
            </p>
          </div>
          {needsReview && <Badge variant="secondary">Review needed</Badge>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline">{display(fields['Document Type']) || 'Unclassified'}</Badge>
          {display(fields['Report Category']) && (
            <Badge variant="secondary">{display(fields['Report Category'])}</Badge>
          )}
          <OutcomeBadge value={fields['Outcome / Determination']} />
        </div>
        {reportName && reportName !== name && <p className="text-sm font-medium">{reportName}</p>}
        <div className="flex flex-wrap gap-1.5">
          <IssueBadge value={fields['Has Issues / Exceedances']} />
          <MitigationBadge value={fields['Has Mitigation / Conditions']} />
        </div>
        {preview && (
          <p className="line-clamp-3 whitespace-pre-line text-sm text-muted-foreground">
            {preview}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
          <span>{formatDate(fields['Document Date']) || 'No document date'}</span>
          <StatusPill value={fields['Processing Status']} />
          <StatusPill value={fields['Review Status']} />
          <ConfidencePill
            value={
              typeof fields['Extraction Confidence'] === 'number'
                ? fields['Extraction Confidence']
                : null
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentTable({
  items,
  onOpen,
}: {
  items: PortalRecord[];
  onOpen(item: PortalRecord): void;
}) {
  const columns = ecorpResources.documents.listFields;
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="min-w-[1900px] text-sm">
        <thead className="bg-muted/60 text-left">
          <tr>
            {columns.map((name, index) => (
              <th
                key={name}
                className={`whitespace-nowrap px-3 py-2 font-medium ${index === 0 ? 'sticky left-0 z-10 bg-muted' : ''}`}
              >
                {name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              className="cursor-pointer border-t hover:bg-muted/30"
              onClick={() => onOpen(item)}
            >
              {columns.map((name, index) => {
                const value = item.fields[name];
                const needsReview =
                  display(item.fields['Processing Status']) === 'Needs Review' ||
                  display(item.fields['Extraction Status']) === 'Needs Review' ||
                  ['Not Reviewed', 'In Review', 'Changes Requested'].includes(
                    display(item.fields['Review Status']),
                  );
                let content: React.ReactNode = display(value) || '—';
                if (name === 'Document Name' && needsReview)
                  content = (
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                        title="Review needed"
                      />
                      {display(value) || 'Untitled document'}
                    </span>
                  );
                else if (name === 'Outcome / Determination')
                  content = <OutcomeBadge value={value} />;
                else if (name === 'Has Issues / Exceedances')
                  content = value === true ? <IssueBadge value /> : 'No';
                else if (name === 'Has Mitigation / Conditions')
                  content = value === true ? <MitigationBadge value /> : 'No';
                else if (name === 'Processing Status' || name === 'Review Status')
                  content = <StatusPill value={value} />;
                else if (name === 'Extraction Confidence')
                  content = <ConfidencePill value={typeof value === 'number' ? value : null} />;
                else if (name === 'Document Date') content = formatDate(value) || '—';
                else if (name === 'Current Version')
                  content = value === true ? <Badge>Current</Badge> : 'No';
                return (
                  <td
                    key={name}
                    className={`max-w-72 truncate px-3 py-2 ${index === 0 ? 'sticky left-0 z-[1] bg-card font-medium' : ''}`}
                    title={display(value)}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DocumentsView({ onContext }: { onContext(value: Record<string, unknown>): void }) {
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const [documentId, setDocumentId] = useState<string>();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filters, setFilters] = useState<DocumentFilters>(emptyFilters);
  const [items, setItems] = useState<PortalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [offset, setOffset] = useState<string>();
  const [nextOffset, setNextOffset] = useState<string>();
  const [sortField, setSortField] = useState(
    ecorpResources.documents.defaultSort?.field ?? 'Uploaded At',
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    ecorpResources.documents.defaultSort?.direction ?? 'desc',
  );
  const [mode, setMode] = useState<ViewMode>(
    () => (localStorage.getItem('ecorp-view-documents') as ViewMode | null) ?? 'cards',
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const openUpload = useCallback(() => setUploadOpen(true), []);
  usePageHeader(
    {
      title: id ? 'Document' : 'Documents',
      subtitle: id
        ? 'Document details, extraction state, and review controls.'
        : 'Technical reports, site plans, permits, proposals, and project feedback.',
      actions: [
        {
          id: 'upload-document',
          label: 'Upload Document',
          icon: <Upload className="h-4 w-4" aria-hidden />,
          variant: 'primary',
          onClick: openUpload,
        },
      ],
    },
    [id, openUpload],
  );
  useEffect(() => {
    const open = params.get('open');
    if (open) setDocumentId(open);
  }, [params]);
  useEffect(() => {
    if (id) setDocumentId(id);
  }, [id]);

  const queryKey = useMemo(
    () => JSON.stringify({ filters, offset, sortField, sortDirection, refreshKey }),
    [filters, offset, sortField, sortDirection, refreshKey],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({ pageSize: '50', sortField, sortDirection });
      for (const [key, value] of Object.entries(filters)) {
        if (key === 'project' || key === 'client') {
          const linked = value as LinkedFilter | undefined;
          if (linked?.id) query.set(key, linked.id);
        } else if (typeof value === 'string' && value.trim()) query.set(key, value.trim());
      }
      if (offset) query.set('offset', offset);
      setLoading(true);
      setError(undefined);
      void listRecords('documents', query)
        .then((page) => {
          setItems(page.items);
          setNextOffset(page.offset);
          onContext({
            resource: 'documents',
            filters: Object.fromEntries(query),
            recordCount: page.items.length,
          });
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : 'Could not load documents.'),
        )
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [queryKey]);

  function updateFilter<K extends keyof DocumentFilters>(key: K, value: DocumentFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
    setOffset(undefined);
  }
  function setView(next: ViewMode) {
    setMode(next);
    localStorage.setItem('ecorp-view-documents', next);
  }
  function openDocument(item: PortalRecord) {
    setDocumentId(item.id);
    onContext({
      resource: 'documents',
      documentId: item.id,
      documentName: display(item.fields['Document Name']),
    });
  }
  const hasFilters = Object.values(filters).some((value) =>
    typeof value === 'string' ? Boolean(value) : Boolean(value?.id),
  );

  return (
    <>
      <section className="space-y-4">
        <Card>
          <CardContent className="space-y-3 p-3">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-72 flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search document content…"
                  value={filters.search}
                  onChange={(event) => updateFilter('search', event.target.value)}
                />
              </div>
              <select
                className="field-control w-56"
                value={sortField}
                onChange={(event) => setSortField(event.target.value)}
              >
                {['Uploaded At', ...ecorpResources.documents.listFields].map((field) => (
                  <option key={field} value={field}>
                    Sort: {field}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                onClick={() => setSortDirection((value) => (value === 'asc' ? 'desc' : 'asc'))}
              >
                {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
              </Button>
              <Button
                variant={mode === 'cards' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setView('cards')}
                aria-label="Card view"
              >
                <Grid2X2 className="h-4 w-4" />
              </Button>
              <Button
                variant={mode === 'list' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setView('list')}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setRefreshKey((value) => value + 1)}
                aria-label="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
              <LinkedRecordFilter
                resource="projects"
                label="Project"
                primaryField="Project Name"
                value={filters.project}
                onChange={(value) => updateFilter('project', value)}
              />
              <LinkedRecordFilter
                resource="clients"
                label="Client"
                primaryField="Client Name"
                value={filters.client}
                onChange={(value) => updateFilter('client', value)}
              />
              <SelectFilter
                label="Document type"
                value={filters.documentType}
                options={documentTypeOptions}
                onChange={(value) => updateFilter('documentType', value)}
              />
              <SelectFilter
                label="Report category"
                value={filters.reportCategory}
                options={reportCategoryOptions}
                onChange={(value) => updateFilter('reportCategory', value)}
              />
              <SelectFilter
                label="Outcome"
                value={filters.outcomeDetermination}
                options={outcomeDeterminationOptions}
                onChange={(value) => updateFilter('outcomeDetermination', value)}
              />
              <SelectFilter
                label="Issues / exceedances"
                value={filters.hasIssuesExceedances}
                options={['true', 'false']}
                onChange={(value) => updateFilter('hasIssuesExceedances', value)}
              />
              <SelectFilter
                label="Mitigation / conditions"
                value={filters.hasMitigationConditions}
                options={['true', 'false']}
                onChange={(value) => updateFilter('hasMitigationConditions', value)}
              />
              <SelectFilter
                label="Processing"
                value={filters.processingStatus}
                options={processingStatusOptions}
                onChange={(value) => updateFilter('processingStatus', value)}
              />
              <SelectFilter
                label="Extraction"
                value={filters.extractionStatus}
                options={extractionStatusOptions}
                onChange={(value) => updateFilter('extractionStatus', value)}
              />
              <SelectFilter
                label="Review"
                value={filters.reviewStatus}
                options={reviewStatusOptions}
                onChange={(value) => updateFilter('reviewStatus', value)}
              />
              <SelectFilter
                label="Current version"
                value={filters.currentVersion}
                options={['true', 'false']}
                onChange={(value) => updateFilter('currentVersion', value)}
              />
              <label className="field-label">
                Date from
                <input
                  className="field-control"
                  type="date"
                  value={filters.documentDateFrom}
                  onChange={(event) => updateFilter('documentDateFrom', event.target.value)}
                />
              </label>
              <label className="field-label">
                Date to
                <input
                  className="field-control"
                  type="date"
                  value={filters.documentDateTo}
                  onChange={(event) => updateFilter('documentDateTo', event.target.value)}
                />
              </label>
            </div>
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasFilters}
                onClick={() => {
                  setFilters(emptyFilters);
                  setOffset(undefined);
                }}
              >
                <X className="mr-1.5 h-4 w-4" />
                Clear filters
              </Button>
            </div>
          </CardContent>
        </Card>
        {error && (
          <ErrorNotice message={error} onRetry={() => setRefreshKey((value) => value + 1)} />
        )}
        {loading ? (
          <Skeletons />
        ) : !items.length ? (
          <EmptyState
            title="No documents match these filters."
            hint={hasFilters ? 'Clear filters to see all documents.' : undefined}
          />
        ) : mode === 'cards' ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <DocumentCard key={item.id} item={item} onOpen={() => openDocument(item)} />
            ))}
          </div>
        ) : (
          <DocumentTable items={items} onOpen={openDocument} />
        )}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{items.length} records on this page</span>
          <div className="flex gap-2">
            <Button variant="outline" disabled={!offset} onClick={() => setOffset(undefined)}>
              First page
            </Button>
            <Button variant="outline" disabled={!nextOffset} onClick={() => setOffset(nextOffset)}>
              Next page
            </Button>
          </div>
        </div>
      </section>
      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => setRefreshKey((value) => value + 1)}
      />
      <DocumentDetail
        documentId={documentId}
        open={Boolean(documentId)}
        onClose={() => {
          setDocumentId(undefined);
          if (params.get('open')) {
            const next = new URLSearchParams(params);
            next.delete('open');
            setParams(next, { replace: true });
          }
        }}
        onChanged={() => setRefreshKey((value) => value + 1)}
      />
    </>
  );
}
